import { html, LitElement } from 'lit';
import { state, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { limitValueToMinMax } from '@nirvana/shared/basic-types/number';

export class GzLoaderWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  // how many lines of loaders we want to display
  @property({ type: Number, reflect: true })
  rows = 1;

  // how many columns of loaders we want to display
  @property({ type: Number, reflect: true })
  columns = 1;

  /**
   * We need to constrain the maximum amount of columns that can be generated with the 'columns' property because the
   * column width is calculated by dividing 100% to the number of columns. We don't want to have unnatural values
   * for the 'columns' property.
   */
  @state()
  maxColumnsCount = 12;

  // setting this to 'true' will display a vertical line between the loader columns
  @property({ type: Boolean, reflect: true })
  splitColumnsByLine = false;

  // setting this to 'true' will display a horizontal line between the loader rows
  @property({ type: Boolean, reflect: true })
  splitRowsByLine = false;

  // add a custom width in pixels for the last column of the loader;
  @property({ type: Number, reflect: true })
  lastColumnPixelWidth;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  /**
   * Convert the rows number into an iterable so we can loop over it and
   * return a loader column for each iteration
   */
  render() {
    return html` ${[...Array(this.rows)].map(() => this.renderColumns())}`;
  }

  /**
   * Convert the columns number into an iterable so we can loop over it and
   * return a loader item for each iteration
   */
  private renderColumns() {
    const columnsCount = limitValueToMinMax(this.columns, 1, this.maxColumnsCount);

    return html`<div class="loader-columns">
      ${[...Array(columnsCount)].map((item, index) => this.renderLoaderItem(columnsCount, index))}
    </div>`;
  }

  private renderLoaderItem(columnsCount: number, columnIndex: number) {
    const loaderClass = columnsCount > 1 ? 'has-columns' : '';
    const verticalBar = this.splitColumnsByLine ? html`<span class="vertical-bar"></span>` : null;

    let loaderTemplate = html`<div class="item ${loaderClass} width-${columnsCount}">
      <div class="loader"></div>
    </div>`;

    /**
     *  set a custom width for the last column if 'lastColumnPixelWidth' is set
     */
    if (this.lastColumnPixelWidth) {
      const spacer = getComputedStyle(document.body).getPropertyValue('--spacer') || '8px';
      let itemWidth = `${this.lastColumnPixelWidth}px`;

      /**
       * To calculate the remaining columns widths, we need to subtract the custom width value from 100% and
       * divide the result to the total number of columns, minus the spacer on both sides of the column
       */
      if (columnIndex < columnsCount - 1) {
        itemWidth = `calc(((100% - ${this.lastColumnPixelWidth}px) / ${columnsCount - 1}) - 2 * ${spacer} )`;
      }

      loaderTemplate = html` <div class="item ${loaderClass}" style="width: ${itemWidth}">
        <div class="loader"></div>
      </div>`;
    }

    return html`${loaderTemplate} ${verticalBar}`;
  }
}
